/**
 * «MIS MACROS», REHECHA CON EL DOCUMENTO DE JESÚS DEL 7-09 («Mis macros, dibujada»).
 *
 * Es la misma pantalla para todos, con un interruptor: el de plan personalizado la LEE y el de
 * autogestión, además, puede tocar sus números. Una pantalla y un interruptor, no dos pantallas
 * que mantener.
 *
 * El orden es el suyo, y cada bloque contesta a una pregunta distinta:
 *
 *   TU FRASE       lo que le dijimos en ESTE ajuste, arriba del todo y firmada con la fecha.
 *                  «Es la pieza»: estaba debajo de los números y ahora abre la pantalla.
 *   LOS TRES DÍAS  entreno, perientreno y descanso AL MISMO NIVEL, sin colores, y con la marca
 *                  HOY en los que son de hoy (en un día de entreno, los dos primeros).
 *   EL HISTÓRICO   los cuatro últimos ajustes; el resto, en «Todo tu histórico» (pantalla 03).
 *   EL PRÓXIMO     de dónde sale el siguiente ajuste, que es distinto según quién mire.
 *
 * LO QUE SE FUE DE AQUÍ, y a dónde: los macros de hoy en grande (están en Inicio y en
 * Nutrición, y allí además con lo que lleva comido), el aviso de macros provisionales (a Mi
 * perfil, que es donde se arregla), la fecha de la próxima revisión repetida dos líneas más
 * abajo, el año en las fechas y el «Último ajuste: −20 g de hidratos en entreno, −10 g de
 * grasa en descanso...» en texto corrido, que con siete cambios era un párrafo.
 *
 * Y lo que se quedó fuera A PROPÓSITO, porque él tiene que decidirlo antes: la gráfica del
 * bloque (el documento dice «barras de hidratos» y el vídeo del mismo día, «los macros sumando
 * todos»), la pastilla del objetivo por fila (el dato solo está en 52 de 3.702 filas de
 * producción) y las horas de la promesa del reporte.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ClipboardCheck } from 'lucide-react';
import { COLOR_MACRO } from '../components/inicio/MacrosHoy';
import { useAuth } from '../context/AuthContext';
import { seLeOfreceLaRevision } from '../lib/revision';
import { MACRO } from './ClientDashboard';
import HistorialDeMacros from '../components/HistorialDeMacros';
import ComparadorDeAjustes from '../components/ComparadorDeAjustes';
import { PERI_OPTIONS } from '../components/nutrition/ConfigSection';
import { num1 } from '../lib/numeros';
import { diasDesdeHoyLocal, hoyLocal } from '../lib/horaEspana';
import { mensajeDeError } from '../lib/mensajeDeError';

// «7 de septiembre», SIN AÑO (Jesús, 7-09: «la fecha repetida y el formato largo» se caen).
// El año no dice nada en un ajuste de hace una semana y hacía que la cabecera ocupara tres
// líneas en el móvil.
const fechaDelAjuste = (iso) => {
    if (!iso) return '';
    return new Date(iso + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
};

// «LLEVAS UNA SEMANA CON ESTOS MACROS» (regla 01·1). La segunda cosa que dice la cabecera, y
// la única que le dice algo: la fecha sola no responde a «¿cuánto llevo así?».
//
// En días del CLIENTE, no del servidor: es su tiempo, no un plazo nuestro
// (`diasDesdeHoyLocal`). Y en palabras, no en número, hasta donde el número no aporta: a
// partir del mes redondear a meses es lo que haría cualquiera al contarlo.
export const tiempoConEstosMacros = (iso) => {
    if (!iso) return null;
    const d = diasDesdeHoyLocal(iso);
    if (d == null || Number.isNaN(d) || d < 0) return null;
    if (d === 0) return 'desde hoy';
    if (d === 1) return 'un día';
    if (d < 7) return `${d} días`;
    if (d < 14) return 'una semana';
    if (d < 28) return `${Math.floor(d / 7)} semanas`;
    if (d < 60) return 'un mes';
    return `${Math.floor(d / 30)} meses`;
};

// CUÁNDO EMPIEZA EL CAMBIO, dicho como lo diría una persona (regla 06·1). «Mañana» mientras
// sea mañana; dentro de la semana, el día con su número («el lunes 15»), que es como él lo
// escribió; y más lejos, la fecha entera. En días del CLIENTE, que es quien lo lee.
//
// Devuelve la frase ENTERA -- «a partir de mañana», «a partir del miércoles 9» -- y no solo el
// día, porque la preposición cambia con el caso: pegando «el miércoles 9» detrás de un «a
// partir de» salía «a partir de el miércoles 9». Visto en pantalla el 7-09.
export const cuandoEmpieza = (iso) => {
    if (!iso) return '';
    const faltan = -diasDesdeHoyLocal(iso);
    if (faltan === 1) return 'a partir de mañana';
    const d = new Date(iso + 'T12:00:00');
    if (faltan > 1 && faltan <= 7) {
        return `a partir del ${d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric' })}`;
    }
    return `a partir del ${d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}`;
};

// CUÁNDO SE ABRE LA VENTANA, CON SU HORA (regla 01·comparador 4): «el miércoles 23 a las
// 10:00». Sin la hora, quien entra ese mismo miércoles por la mañana ve un botón apagado con
// la fecha de hoy y no entiende por qué no puede pulsarlo.
//
// La hora la manda el servidor (`ventana_revision.hora_de_apertura`, que la saca del reloj del
// calendario) y si no llega, se dice solo el día: mejor quedarse corto que inventarse una hora.
export const cuandoSeAbre = (iso, hora) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    // El día, con la misma regla que `cuandoEmpieza`: dentro de la semana basta el día de la
    // semana («miércoles 23»), y más lejos hace falta el mes («23 de septiembre»). Y sin la
    // coma que mete el formato largo -- «lunes, 14 de septiembre» --, que dentro de la frase
    // «Se abre el ... a las 10:00» parte en dos lo que es una sola cosa.
    const faltan = -diasDesdeHoyLocal(iso);
    const dia = faltan > 0 && faltan <= 7
        ? d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric' })
        : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
    return hora == null ? dia : `${dia} a las ${String(hora).padStart(2, '0')}:00`;
};

// «130P · 60H · 50G»: los tres de un bloque en una línea, para meterlos dentro de una frase
// («nuestra sugerencia era 130P · 60H · 50G y comes 130P · 95H · 50G», regla 07·1).
// Con su letra desde el 8-09 (Francisco), como en el resto de la app.
const tresNumeros = (m) => (m ? `${num1(m.proteina)}P · ${num1(m.hidratos)}H · ${num1(m.grasa)}G` : '');

// Los tres macros de un bloque, en el orden de siempre.
const CAMPOS = [
    { key: 'proteina', corto: 'P', label: 'Proteína', color: MACRO.protein },
    { key: 'hidratos', corto: 'H', label: 'Hidratos', color: MACRO.carbs },
    { key: 'grasa', corto: 'G', label: 'Grasa', color: MACRO.fat },
];

// Con coma y a la décima, con la función de la casa (punto 71): iba en crudo, y un macro
// con decimales habría salido «112.5».
const numero = (v) => (v === null || v === undefined ? '-' : num1(v));

// LOS TRES TIPOS DE DÍA AL MISMO NIVEL, Y SIN COLORES (Jesús, 7-09, reglas 01·4 y 01·5).
//
// Antes había uno en grande -- el que tocaba hoy -- y los otros dos en dos líneas pequeñas.
// Su diagnóstico, literal: «así ve su día pero no entiende su plan». Ahora los tres van con
// el mismo peso y lo que distingue al de hoy es la marca HOY, no el tamaño.
//
// Y EN UN DÍA DE ENTRENO SE MARCAN LOS DOS, el día de entreno y el perientreno, «porque los
// dos son de hoy»: el peri nunca llevaba marca, así que el que entrena leía su comida del
// entreno como si fuera de otro día.
//
// Sin colores por macro: «el color es para el estado, no para el macro». Los números en
// blanco y los separadores en gris, que es lo que deja leer los tres de un vistazo.
/** «de los que 30 · 15 son del perientreno», la línea pequeña de la decisión 4.
 *
 *  Sale sólo si ese día lleva perientreno APARTE. En modo `sin_peri` su presupuesto va
 *  repartido dentro de las comidas y no hay ninguna toma que desglosar: decirlo entonces
 *  sería nombrarle algo que ese día no se toma. Es el caso de la cuenta de Jesús, y por eso
 *  a él el interruptor de Inicio tampoco le sale.
 */
const desgloseDelPeri = (peri) => {
    if (!peri) return null;
    const p = peri.proteina;
    const h = peri.hidratos;
    if (p == null && h == null) return null;
    const trozos = [p != null ? numero(p) : null, h != null ? numero(h) : null].filter(Boolean);
    if (!trozos.length) return null;
    return `de los que ${trozos.join(' · ')} son del perientreno`;
};

/** EL DÍA DE ENTRENO CON EL PERI DENTRO, TAMBIÉN EN EL CAMBIO PROGRAMADO (Jesús, 11-09, punto 12).
 *
 *  Su queja, mirando el cambio que le entraba el lunes: «Los dos días con 170 · 160 · 60, y el
 *  de entreno además "de los que 45 · 30 son del perientreno". Si el entreno lleva 45 de peri
 *  dentro, el descanso no puede dar el mismo número. Los de ahora sí se diferencian:
 *  160 · 75 · 50 y 140 · 60 · 50».
 *
 *  Ni estaba mal calculado ni mal guardado: estaba mal PINTADO, y las dos filas que él compara
 *  no medían lo mismo. El bloque de los macros de ahora pinta `dia_entreno`, que es el total
 *  del día con el peri sumado (lo manda el servidor), y el del cambio programado se quedaba en
 *  `entreno`, que son solo las COMIDAS, porque en ese bloque el servidor no manda el total. El
 *  ajuste es correcto -- las comidas del entreno empatan con el descanso y el peri es lo que
 *  pone al día de entreno por encima, que es la regla del método: comidas contra comidas --,
 *  pero enseñado así salían dos días iguales y encima con una coletilla que era mentira sobre
 *  ese número. En la base de dev hay 129 ajustes con esa forma exacta.
 *
 *  Se suma aquí porque el bloque programado no trae el total. Los números llegan ya redondeados
 *  a entero del servidor, así que esto no puede ser más fino que ellos; lo que arregla de raíz
 *  es que el servidor mande `dia_entreno` también en `programado` (routes/users.py), y en cuanto
 *  lo mande esta suma deja de usarse sola.
 *
 *  LA GRASA NO LLEVA PERI, igual que en el resto de la app: el perientreno no tiene.
 */
export const totalDelDia = (entreno, peri) => {
    if (!entreno) return entreno;
    if (!peri) return entreno;
    const suma = (a, b) => {
        if (a == null && b == null) return null;
        // A la décima, la regla única de la casa.
        return Math.round(((a || 0) + (b || 0) + 1e-9) * 10) / 10;
    };
    return {
        proteina: suma(entreno.proteina, peri.proteina),
        hidratos: suma(entreno.hidratos, peri.hidratos),
        grasa: entreno.grasa,
    };
};

const FilaDeDia = ({ label, macros, conGrasa = true, hoy = false, desglose = null }) => {
    const campos = conGrasa ? CAMPOS : CAMPOS.slice(0, 2);
    return (
        <div className={`flex items-center justify-between gap-3 py-2.5 px-3 rounded-xl border ${
            hoy ? 'border-brand/45 bg-brand/[0.06]' : 'border-transparent'}`}>
            <div className="min-w-0">
                {hoy && (
                    <p className="text-[10px] font-bold uppercase tracking-wider text-brand leading-none mb-1">Hoy</p>
                )}
                <p className={`text-sm leading-tight ${hoy ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    {label}
                </p>
                {/* EL DESGLOSE DEBAJO, EN PEQUEÑO (decisión 4 de Jesús, 8-09-2026):
                    «de los que 30 · 15 son del perientreno». El número grande es el día
                    entero, igual que en Inicio, Mi semana y Nutrición; el desglose se queda
                    donde se estudia, que es aquí. Sin interruptor: «añade un sitio más
                    donde fallar y no hace falta si el total está siempre delante». */}
                {desglose && (
                    <p className="text-xs text-muted-foreground/80 leading-tight mt-0.5"
                       data-testid="macros-desglose-peri">
                        {desglose}
                    </p>
                )}
            </div>
            <p className="font-data text-base font-bold text-foreground whitespace-nowrap">
                {campos.map((c, i) => (
                    <React.Fragment key={c.key}>
                        {i > 0 && <span className="text-muted-foreground/60 font-normal mx-1.5">·</span>}
                        {numero(macros?.[c.key])}
                    </React.Fragment>
                ))}
            </p>
        </div>
    );
};

// ── EL REDISEÑO DEL 17-10 (handoff «Mis macros», docs/diseño/design_handoff_mis_macros) ──
// Un día cada vez, elegido con el selector (al entrar, el de hoy, con su HOY), una barra con
// el reparto por kcal (sin enseñar las kcal: hoja del 22-10, fila 57), las tres tarjetas de macro y,
// solo en día de entreno, cuánto del día es perientreno. Los colores de cada macro son los del
// diagrama de Inicio (COLOR_MACRO), que es donde el cliente ya los ha aprendido.
const kcalDe = (m) => (m ? (m.proteina || 0) * 4 + (m.hidratos || 0) * 4 + (m.grasa || 0) * 9 : 0);
// Los mismos tres de CAMPOS (rótulos de siempre), con sus kcal por gramo y el color de Inicio.
const KCAL_POR_GRAMO = { proteina: 4, hidratos: 4, grasa: 9 };
const MACROS_DEL_DIA = CAMPOS.map((c) => ({ ...c, kcal: KCAL_POR_GRAMO[c.key], color: COLOR_MACRO[c.corto] }));

const TusMacrosDelDia = ({ entreno, descanso, peri, resto, hoy, conPeri }) => {
    // Al entrar, el de hoy; si no se sabe qué toca hoy, el de entreno, sin la marca HOY.
    const [dia, setDia] = useState(hoy === 'descanso' ? 'descanso' : 'entreno');
    const macros = dia === 'entreno' ? entreno : descanso;
    const kcal = kcalDe(macros);
    const reparto = MACROS_DEL_DIA.map((c) => {
        const k = (macros?.[c.key] || 0) * c.kcal;
        return { ...c, k, pct: kcal > 0 ? Math.round((k / kcal) * 100) : 0 };
    });
    const esHoy = (d) => (d === 'entreno' ? hoy === 'entrenamiento' : hoy === 'descanso');
    const kcalPeri = ((peri?.proteina || 0) + (peri?.hidratos || 0)) * 4;
    return (
        <div className="space-y-[18px]" data-testid="mis-macros-hoy">
            <div className="grid grid-cols-2 gap-1 rounded-[14px] bg-[#1a1a1a] border border-[#262626] p-1" role="radiogroup" aria-label="Tipo de día">
                {[['entreno', 'Día de entreno'], ['descanso', 'Día de descanso']].map(([d, texto]) => {
                    const activo = dia === d;
                    return (
                        <button key={d} type="button" role="radio" aria-checked={activo} onClick={() => setDia(d)}
                            data-testid={`mis-macros-dia-${d}`}
                            className={`h-11 rounded-[10px] text-sm font-bold flex items-center justify-center gap-1.5 transition-colors ${
                                activo ? 'bg-brand text-[#140a04]' : 'text-[#d9d5d0] hover:text-foreground'}`}>
                            {texto}
                            {esHoy(d) && (
                                <span className={`text-[10px] font-extrabold rounded-md px-1.5 py-0.5 text-[#ff8a4d] ${activo ? 'bg-[#140a04]' : 'bg-[#2a1b10]'}`}>HOY</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <section className="rounded-[20px] bg-[#161616] border border-[#262626] p-[18px] space-y-4">
                {/* SIN CALORÍAS (hoja del 22-10, fila 57: «Eliminar calorías de toda la
                    app»). Aquí estaban el total del día, la diferencia con el otro día y las
                    kcal de cada macro, que eran las únicas que veía el cliente. Las kcal se
                    siguen calculando por dentro solo para el reparto en porcentaje. */}
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8580]">
                    {dia === 'entreno' ? 'Día de entreno' : 'Día de descanso'}{esHoy(dia) ? ' · Hoy' : ''}
                </p>

                <div>
                    <div className="flex gap-0.5 h-2.5 rounded-[5px] overflow-hidden" aria-hidden="true">
                        {reparto.map((c) => (
                            <span key={c.key} className="h-full transition-[width] duration-[350ms]"
                                style={{ width: `${c.pct}%`, backgroundColor: c.color }} />
                        ))}
                    </div>
                    <p className="text-[11px] text-[#8a8580] mt-1.5">
                        {reparto.map((c) => `${c.pct}% ${c.corto}`).join(' · ')}
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-2">
                    {reparto.map((c) => (
                        <div key={c.key} className="rounded-[14px] bg-[#1f1f1f] px-2.5 py-3 border-t-[3px]" style={{ borderTopColor: c.color }}
                            data-testid={`mis-macros-${c.key}`}>
                            <p className="text-xs font-bold text-[#d9d5d0]">{c.label}</p>
                            <p className="leading-none mt-1.5">
                                <span className="font-heading text-[34px] font-bold text-foreground">{numero(macros?.[c.key])}</span>
                                <span className="text-sm font-semibold text-[#8a8580]"> g</span>
                            </p>
                        </div>
                    ))}
                </div>

                {/* EL PERIENTRENO, SOLO EN DÍA DE ENTRENO Y SI LO LLEVA APARTE: en `sin_peri` sus
                    macros van dentro de las comidas y no hay toma que desglosar. */}
                {dia === 'entreno' && conPeri && (
                    <div className="rounded-[14px] bg-[#1f1f1f] px-3.5 py-3 space-y-2" data-testid="macros-desglose-peri">
                        <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8580]">Reparto del día</p>
                        {/* «Comidas» arriba, la barra del peri en medio y «Perientreno» debajo
                            (Enrique, 29-10). Antes «Resto de comidas», debajo del peri. */}
                        <div className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="text-[#d9d5d0]">Comidas</span>
                            <span className="font-data font-bold text-foreground">{numero(resto?.proteina)} · {numero(resto?.hidratos)}</span>
                        </div>
                        <div className="h-1 rounded-full bg-[#2c2c2c] overflow-hidden">
                            <div className="h-full rounded-full bg-brand" style={{ width: `${kcal > 0 ? Math.min(100, (kcalPeri / kcal) * 100) : 0}%` }} />
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="text-[#d9d5d0]">Perientreno</span>
                            <span className="font-data font-bold text-foreground">{numero(peri?.proteina)} · {numero(peri?.hidratos)}</span>
                        </div>
                    </div>
                )}
            </section>

            {/* AQUÍ IBA «Ver lo que llevas comido hoy», el enlace a Mi día (regla 01·6), Y SE
                QUITA (29-10). */}
        </div>
    );
};

// La tabla del histórico vive en components/HistorialDeMacros, compartida con Evolución.

// EL RÓTULO DEL PERI, CON EL MODO QUE TIENE PUESTO EL CLIENTE (punto 50 del doc 24-08).
//
// Aquí ponía «Perientreno (intra + post)» escrito a fuego, sin mirar su configuración:
// Montalvo tiene «solo post» guardado en su ficha y en sus diez dietas de agosto, así que
// Nutrición le decía «solo post» y esta pantalla «intra + post» el mismo día. Los gramos
// no se tocan, solo el nombre.
//
// Las etiquetas son las del desplegable donde él lo elige (PERI_OPTIONS de ConfigSection),
// que es la misma lista que usa la línea de configuración de Nutrición: así la misma cosa
// se llama igual en las dos pantallas. Ojo, que hay OTRA lista `PERI_OPTIONS` en
// components/nutrition/constants.js, con «Sin periworkout» y sin que la importe nadie:
// renombrar ahí no cambia ni esta pantalla ni Nutrición.
export const rotuloDelPeri = (opcionPeri) => {
    // «Sin perientreno» no es un matiz del rótulo, es lo contrario: ahí el bloque se
    // queda en «Perientreno» a secas. Y sin saber su modo tampoco se inventa uno: mejor
    // no decirlo que decir el que no es, que es justo lo que pasaba. Sin modo se está
    // solo mientras carga o si la consulta falla: al que no ha elegido nunca, el
    // servidor le contesta `intra_post`, el mismo que da por bueno Nutrición.
    if (!opcionPeri || opcionPeri === 'sin_peri') return 'Perientreno';
    const etiqueta = PERI_OPTIONS.find(o => o.value === opcionPeri)?.label;
    return etiqueta ? `Perientreno (${etiqueta.toLowerCase()})` : 'Perientreno';
};

// De dónde sale ese modo. NO de `profile`: el modelo con el que el servidor sirve
// /clients/profile (ClientProfile) no lleva `diet_opcion_peri`, así que en el front
// siempre llegaba undefined. Se pide a /user/diet-config, que es de donde ya lo sacan
// Inicio (TuDietaHoy) y el chat cuando el día no está montado.
//
// El de la FICHA, que es su última elección: Nutrición lo guarda ahí cada vez que toca el
// desplegable. Un día concreto puede tener guardado otro (el que había cuando se montó) y
// eso es lo que enseña Nutrición en ESE día; aquí no se habla de un día, se habla de sus
// macros, así que manda lo que el cliente lleva puesto.
export const useOpcionPeri = () => {
    const { api } = useAuth();
    const [opcionPeri, setOpcionPeri] = useState(null);
    useEffect(() => {
        let vivo = true;
        api.get('/user/diet-config')
            .then(r => { if (vivo) setOpcionPeri(r.data?.opcion_peri || null); })
            // Sin esto la pantalla se pinta igual, con el rótulo corto: el porqué, a la consola.
            .catch(e => console.error('No se pudo leer la configuración del perientreno', e));
        return () => { vivo = false; };
    }, [api]);
    return opcionPeri;
};

const MisMacrosPage = ({ onAjustar, onCancelado }) => {
    const { api, profile, can } = useAuth();
    const navigate = useNavigate();
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(true);
    // EL AVISO DE MACROS PROVISIONALES YA NO VIVE AQUÍ (Jesús, 7-09, «lo que se cae · 6»):
    // «es un problema de ficha metido en la pantalla de comer», el mismo criterio que ya se
    // aplicó en Nutrición. Su sitio es Mi perfil, donde se arregla, con el punto naranja en
    // el menú -- y ahí ya estaba (ProfilePage + ClientDashboard). Lo que pasaba es que no se
    // había quitado de los otros dos sitios, así que salía tres veces.
    // La ventana del botón Revisar: {abierta, se_abre, motivo}, del servidor (tarea 7.3).
    const ventana = profile?.ventana_revision;
    // Aquí se calculaba «el miércoles 23», el día en que abre su próximo reporte, para la
    // frase que se quitó el 16-09. `ventana` se queda: la usa el botón de Revisar.
    // Su perientreno, para rotular el bloque con el que de verdad lleva (punto 50, 24-08).
    const opcionPeri = useOpcionPeri();

    useEffect(() => {
        let vivo = true;
        // CON SU DÍA (10-09). El servidor marcaba «HOY» según el reloj de la máquina en
        // UTC, así que de madrugada señalaba el bloque del día siguiente: Mis macros ponía
        // HOY en «Día de descanso» mientras Inicio decía que hoy entrenas. El día vivido
        // es el del navegador, y el servidor solo lo valida.
        api.get(`/macros/historial?dia=${hoyLocal()}`)
            .then(res => { if (vivo) setDatos(res.data); })
            .catch(() => { if (vivo) setDatos(null); })
            .finally(() => { if (vivo) setCargando(false); });
        return () => { vivo = false; };
    }, [api]);

    const vigente = datos?.vigente;
    const entradas = datos?.entradas || [];
    // El cambio con fecha por delante, si lo hay (pantalla 06). Lo devuelve el servidor
    // aparte del histórico: el histórico cuenta lo que ya pasó.
    const programado = datos?.programado;
    // Lo que le habíamos sugerido nosotros antes de que él lo moviera (pantalla 07). El
    // servidor solo lo manda cuando el ajuste vigente es suyo: si se lo pusimos nosotros, no
    // hay dos versiones que comparar.
    const sugerencia = datos?.sugerencia_anterior;
    const [cancelando, setCancelando] = useState(false);

    // «CANCELARLO». Se recarga la pantalla entera con lo que conteste el servidor en vez de
    // tocar el estado a mano: si el borrado no llega a hacerse, la pantalla no puede quedarse
    // diciendo que sí. Y el motivo del fallo se le da en su idioma (`mensajeDeError`).
    const cancelarProgramado = async () => {
        setCancelando(true);
        try {
            await api.delete('/macros/programado');
            // Con su día, como la carga de arriba (14-10, MAC-01): sin él el servidor marcaba
            // HOY con el día de España y, tras cancelar, la marca saltaba de fila.
            const res = await api.get(`/macros/historial?dia=${hoyLocal()}`);
            setDatos(res.data);
            // Quien tiene el formulario (la calculadora) lo devuelve a los macros de ahora.
            onCancelado?.();
            toast.success('Cancelado. Sigues con los macros de ahora.');
        } catch (e) {
            toast.error(mensajeDeError(e, 'No hemos podido cancelar el cambio'));
        } finally {
            setCancelando(false);
        }
    };
    const entrenaHoy = datos?.tipo_dia_hoy === 'entrenamiento';
    const descansaHoy = datos?.tipo_dia_hoy === 'descanso';
    // «Llevas una semana con estos macros», la segunda mitad de la cabecera (regla 01·1).
    const llevaCon = tiempoConEstosMacros(vigente?.fecha);
    // QUIÉN ESTÁ MIRANDO, PARA LOS TEXTOS: el MODO DE SU PLAN, no si tiene botón de cambiar.
    //
    // No es lo mismo y por eso se separa: a un cliente de plan personalizado que todavía no
    // tenía ningún ajuste de una persona SÍ le llegaba `onAjustar` -- eran sus macros de arranque
    // y podía recalcularlos --, y con `onAjustar` como criterio le
    // salía el texto del de autogestión: «puedes volver a solicitar la revisión de tus macros»,
    // cuando a él el ajuste le llega solo, con su reporte. Visto en pantalla con una cuenta Gold.
    //
    // DESDE EL 12-10 YA NO LE LLEGA (decisión de Francisco; la regla, en
    // core/quien_pone_los_macros): con entrenador, en cuanto tiene los macros de su alta el
    // servidor manda `macros_ajustables.puede: false` y esta pantalla se pinta sin "Cambiar mis
    // macros". Quién se los pone y cuándo se lo dicen la cabecera ("Los calculamos con tu
    // cuestionario el...") y el bloque "Tu próximo ajuste" ("Sale de tu reporte..."), que ya
    // estaban escritos para el cliente al que se los lleva su entrenador. Aquí no se decide nada.
    //
    // `modo_calculadora` lo resuelve el servidor con el catálogo del panel, que es el mismo que
    // decide el candado. Mientras carga no se pinta nada de esto.
    const esAutogestion = datos?.modo_calculadora === 'autogestion';

    return (
        // EL MISMO MARCO Y LA MISMA CABECERA QUE INICIO, MI SEMANA, NUTRICIÓN, MI RUTINA Y
        // ALIMENTOS (15-10): mismo ancho y márgenes, rótulo naranja y título en minúscula.
        <div className="px-4 sm:px-6 lg:px-12 xl:px-20 py-6 max-w-[1400px] mx-auto space-y-6 animate-fade-in">
            <header className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                    <p className="text-xs font-bold uppercase tracking-widest text-brand mb-2">Tus objetivos</p>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground leading-none mb-2" data-testid="macros-heading">
                        Mis macros
                    </h1>
                    {/* LA CABECERA DICE DOS COSAS Y NADA MÁS (Jesús, 7-09, reglas 01·1 y 02·1).
                        Cuándo fue el último ajuste y cuánto lleva con él.

                        Se van tres cosas que estaban aquí y él quita: «Próxima: 28 de
                        septiembre», que repetía la fecha del botón de abajo dos líneas después;
                        el año en las fechas, que no dice nada y comía tres líneas en el móvil;
                        y el «Último ajuste: −20 g de hidratos en entreno, −10 g de grasa en
                        descanso...», que con siete cambios era un párrafo -- «lo dice la
                        escalera, y mejor».

                        «De macros» solo en autogestión, que es como lo dibujó: al que se los
                        lleva un entrenador se le habla de SU ajuste, no del ajuste de una cosa. */}
                    {vigente?.fecha && (
                        <p className="text-sm text-muted-foreground mt-1" data-testid="macros-revisiones">
                            {/* «LA CABECERA CAMBIA: los cambiaste tú el 9 de septiembre, no
                                último ajuste. La fecha que manda es la suya» (regla 07·3).
                                Cuando los movió él, la pantalla no puede seguir hablando como
                                si el ajuste fuera nuestro. */}
                            {vigente.del_cuestionario
                                ? <>Los calculamos con tu cuestionario el{' '}
                                    <span className="font-semibold text-foreground">{fechaDelAjuste(vigente.fecha)}</span></>
                                : vigente.del_cliente
                                ? <>Los cambiaste tú el{' '}
                                    <span className="font-semibold text-foreground">{fechaDelAjuste(vigente.fecha)}</span></>
                                : <>Último ajuste{esAutogestion ? ' de macros' : ''}{' '}
                                    <span className="font-semibold text-foreground">{fechaDelAjuste(vigente.fecha)}</span></>}
                        </p>
                    )}
                    {llevaCon && (
                        <p className="text-sm text-muted-foreground" data-testid="macros-tiempo-con-estos">
                            Llevas <span className="font-semibold text-foreground">{llevaCon}</span> con estos macros
                        </p>
                    )}
                    {/* AQUÍ IBA «Tu próximo ajuste sale de tu reporte del viernes 18», Y SE
                        QUITA (Francisco, 16-09).

                        Venía de la decisión 5 de Jesús del 8-09: la pantalla solo hablaba de
                        la fecha en que ENTRAN los macros que ya están decididos, y esa se
                        leía como si fuera la próxima revisión, así que se añadió la otra para
                        separarlas. Hoy se cae, y con ella la cuenta de `proximoReporte`.

                        Lo que se pierde, dicho: la pantalla vuelve a tener una sola fecha, la
                        del último ajuste. Cuándo se deciden los siguientes se lee en
                        Seguimiento, que es donde está el reporte. */}
                </div>
            </header>

            {cargando ? (
                <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
            ) : (
                <>
                    {/* AQUÍ IBA «Lo que te dijimos en este ajuste», la nota del entrenador en su
                        ajuste, Y SE QUITA (29-10): no va a haber feedback del entrenador sobre
                        macros. */}

                    {/* ── LOS HAS CAMBIADO TÚ (pantalla 07 del doc del 7-09) ──
                        Desde el día en que su cambio entra en vigor. Se ve lo que come de
                        verdad y, al lado, lo que le habíamos sugerido nosotros.

                        Su razón: «sin esto, dentro de un mes nadie sabe si el peso se movió con
                        vuestra sugerencia o con la suya. Y esa es exactamente la pregunta que
                        hay que contestar para hacer el siguiente».

                        DICHA SIN RIÑA, que es la mitad del punto: se le cuenta la diferencia y
                        la consecuencia -- que el mes que viene ajustamos sobre lo suyo -- y se
                        le deja la puerta abierta a decir que no. Sin la sugerencia anterior
                        guardada no se pinta la comparación: no hay dos versiones que comparar,
                        y decirle «los has cambiado tú» a secas no aporta nada. */}
                    {vigente?.del_cliente && sugerencia && (
                        <section className="surface p-4 sm:p-5 space-y-2.5 border-l-4 border-l-brand"
                            data-testid="mis-macros-los-cambiaste-tu">
                            <p className="text-sm text-foreground">
                                <span className="font-semibold">Los has cambiado tú.</span>{' '}
                                Nuestra sugerencia era {tresNumeros(sugerencia.entreno)} y comes{' '}
                                {tresNumeros(vigente.entreno)}.
                            </p>
                            {/* Los dos botones de la regla 07·5. «Cambiarlos» abre el editor;
                                «Volver a los anteriores» lo abre con los de antes cargados, que
                                es lo mismo que hacer el camino de vuelta a mano. No se deshace
                                por detrás: cada vuelta atrás es un ajuste suyo más, y así su
                                histórico sigue contando lo que de verdad pasó. */}
                            {onAjustar && (
                                <div className="flex gap-2 flex-wrap pt-1">
                                    <button type="button" onClick={() => onAjustar()} data-testid="cambiarlos"
                                        className="flex-1 min-w-[130px] py-2.5 rounded-xl border border-border text-foreground text-sm font-semibold hover:border-brand hover:text-brand transition-colors">
                                        Cambiarlos
                                    </button>
                                    <button type="button" onClick={() => onAjustar('anteriores')}
                                        data-testid="volver-a-los-anteriores"
                                        className="flex-1 min-w-[130px] py-2.5 rounded-xl border border-border text-muted-foreground text-sm font-semibold hover:border-foreground hover:text-foreground transition-colors">
                                        Volver a los anteriores
                                    </button>
                                </div>
                            )}
                        </section>
                    )}

                    {/* ── TIENES UN CAMBIO PROGRAMADO (pantalla 06 del doc del 7-09) ──
                        Lo que ve entre que lo guarda y el día en que empieza.

                        Existe porque sin esto la pantalla se quedaba MUDA: reproducido en la
                        app el 7-09, guardas un ajuste con fecha de mañana y Mis macros sigue
                        enseñando los números viejos, sin una palabra. Su diagnóstico: «el
                        cliente guarda algo y luego no ve nada: piensa que no se ha guardado y
                        lo vuelve a hacer».

                        Los ocho números, no tres (vídeo, 5:37: «se ponen siempre los 8 números,
                        no sé por qué ponen solamente los 3»), así que el cambio se enseña con
                        sus tres filas y no solo con la del día de entreno.

                        Y los botones solo si el cambio es SUYO: si se lo pusimos nosotros, el
                        bloque se le enseña igual -- «hasta que llega tiene que ver los de
                        ahora» -- pero no lo puede tirar. El servidor lo cierra también. */}
                    {programado && (
                        <section className="surface p-4 sm:p-5 space-y-3 border border-brand/40"
                            data-testid="mis-macros-programado">
                            <div>
                                <p className="caption text-brand">Tienes un cambio programado</p>
                                <p className="text-sm text-foreground mt-1.5">
                                    <span className="font-semibold first-letter:uppercase">{cuandoEmpieza(programado.fecha)}</span>{' '}
                                    pasas a estos macros.
                                    {programado.del_cliente && programado.guardado_el && (
                                        <> Lo cambiaste tú el {fechaDelAjuste(programado.guardado_el)}.</>
                                    )}
                                </p>
                            </div>
                            <div className="space-y-1">
                                {/* CON EL PERI DENTRO, como el bloque de los de ahora (punto
                                    12 de Jesús del 11-09): si el servidor manda el total lo
                                    usa, y si no lo suma aquí. Sin esto las dos filas no medían
                                    lo mismo y el cambio salía con «los dos días iguales». */}
                                <FilaDeDia label="Día de entreno"
                                    macros={programado.dia_entreno
                                        || totalDelDia(programado.entreno, programado.peri)}
                                    desglose={desgloseDelPeri(programado.peri)} />
                                <FilaDeDia label="Día de descanso" macros={programado.descanso} />
                            </div>
                            {programado.del_cliente && onAjustar && (
                                <div className="flex gap-2 flex-wrap">
                                    <button type="button" onClick={() => onAjustar('programado', programado)} data-testid="modificar-programado"
                                        className="flex-1 min-w-[130px] py-2.5 rounded-xl border border-border text-foreground text-sm font-semibold hover:border-brand hover:text-brand transition-colors">
                                        Modificarlo
                                    </button>
                                    <button type="button" onClick={cancelarProgramado} disabled={cancelando}
                                        data-testid="cancelar-programado"
                                        className="flex-1 min-w-[130px] py-2.5 rounded-xl border border-border text-muted-foreground text-sm font-semibold hover:border-foreground hover:text-foreground transition-colors disabled:opacity-50">
                                        {cancelando ? 'Cancelando...' : 'Cancelarlo'}
                                    </button>
                                </div>
                            )}
                        </section>
                    )}

                    {/* ── TUS MACROS, UN DÍA CADA VEZ (handoff del 17-10) ── Sustituye a los tres días
                        en filas. El de entreno es el total del día con el peri dentro, como en las
                        otras pantallas (decisión 4 de Jesús, 8-09), y «Comidas» (antes «Resto de comidas») es lo que
                        queda sin el peri (`entreno`, que el servidor manda aparte). */}
                    {programado && vigente && (
                        <p className="text-sm text-muted-foreground">Hasta entonces, estos son tus macros:</p>
                    )}
                    {vigente ? (
                        <TusMacrosDelDia
                            entreno={vigente.dia_entreno || totalDelDia(vigente.entreno, vigente.peri)}
                            descanso={vigente.descanso}
                            peri={vigente.peri}
                            resto={vigente.entreno}
                            hoy={datos?.tipo_dia_hoy}
                            conPeri={opcionPeri !== 'sin_peri'
                                && ((vigente.peri?.proteina || 0) > 0 || (vigente.peri?.hidratos || 0) > 0)} />
                    ) : (
                        <section className="surface p-4 sm:p-5" data-testid="mis-macros-hoy">
                            <p className="text-sm text-muted-foreground py-1">
                                Todavía no tienes ningún ajuste guardado. En cuanto te pongamos
                                los primeros, los verás aquí.
                            </p>
                        </section>
                    )}

                    {/* «CAMBIAR MIS MACROS», DEBAJO Y DISCRETO (regla 02·2): «primero se lee lo
                        que tiene, luego se toca». Solo le llega `onAjustar` a quien puede. */}
                    {vigente && onAjustar && (
                        <button type="button" onClick={() => onAjustar()} data-testid="ajustar-mis-macros"
                            className="w-full py-2.5 rounded-xl border border-border text-foreground text-sm font-semibold hover:border-brand hover:text-brand transition-colors">
                            Cambiar mis macros
                        </button>
                    )}

                    {/* ── TU HISTÓRICO ── El componente compartido con Evolución: la misma
                        tabla se pinta aquí para quien tiene la pestaña, y en Seguimiento
                        para quien no (doc 19-08). */}
                    <HistorialDeMacros entradas={entradas} />

                    {/* ── COMPARAR DOS AJUSTES (el comparador de la pantalla 01) ──
                        Entre el histórico y el próximo ajuste, que es donde lo dibujó: la
                        escalera enseña la tendencia y esto contesta a la pregunta concreta,
                        «¿qué cambió exactamente de aquel ajuste a este?». */}
                    <ComparadorDeAjustes entradas={entradas} />

                    {/* ── TU PRÓXIMO AJUSTE (Jesús, 7-09, reglas 01·comparador 4 y 02·4) ──
                        Era un botón suelto arriba, en la cabecera, con la fecha repetida dos
                        líneas más abajo. Ahora es un bloque al final, que es donde lo dibujó, y
                        DICE DE DÓNDE SALE, que es lo que le faltaba.

                        Y dice cosas distintas según quién mire, porque no es lo mismo:
                          - al de plan personalizado el ajuste le LLEGA, y sale de su reporte;
                          - el de autogestión tiene que PEDIRLO, y por eso se le dice desde
                            cuándo puede.

                        LO QUE NO ESTÁ Y NO SE INVENTA: las horas. Él pide «se abre el miércoles
                        23 a las 10:00» y «antes del viernes a las 15:00». La primera existe
                        (core/calendario_reportes.HORAS_DEL_DOC: el quincenal abre a las 10:00) y
                        hay que subirla al perfil para pintarla; la segunda NO existe en ninguna
                        parte -- la promesa de core/promesa_del_reporte es por día, «antes del
                        viernes», sin hora --, así que ponerla aquí sería prometer algo que la
                        app no sabe cumplir. Se deja el día, que es verdad. */}
                    {/* AL CLIENTE CON ENTRENADOR, SOLO EL BOTÓN (29-10): sin el título y sin sus
                        frases («Ya puedes mandar tu reporte...», «Ya nos lo has mandado...», «Sale
                        de tu reporte...», «Se te pasó el plazo...»). El botón «Revisar mis
                        macros» se queda, y sin botón que enseñar no se pinta la caja vacía. */}
                    {ventana && (esAutogestion || ventana.abierta || ventana.se_abre) && (
                        <section className="surface p-5 space-y-3" data-testid="mis-macros-proximo-ajuste">
                            {/* Con entrenador, el título vuelve sin las frases (29-10). */}
                            {!esAutogestion && <p className="caption">Próximo ajuste</p>}
                            {esAutogestion && (<>
                            <p className="caption">Tu próximo ajuste previsto</p>
                            <p className="text-sm text-muted-foreground">
                                {ventana.abierta
                                        ? 'Ya puedes solicitar la revisión de tus macros.'
                                        /* SU FRASE, SIN PREFIJO: «puedes VOLVER A solicitar» ya
                                           dice que hizo una, y anteponerle «ya has solicitado tu
                                           revisión» dejaba «solicitar la revisión» dos veces en
                                           dos renglones. Al de autogestión la ventana cerrada
                                           siempre es porque la hizo, así que no hay otro caso. */
                                        : ventana.se_abre
                                            ? <>{ventana.del_alta ? 'Podrás solicitar' : 'Puedes volver a solicitar'} la revisión de tus macros el{' '}
                                                <span className="font-semibold text-foreground">{cuandoSeAbre(ventana.se_abre, ventana.hora_abre)}</span>.</>
                                            : ventana.motivo}
                            </p>
                            </>)}
                            {ventana.abierta ? (
                                <button type="button" data-testid="boton-revisar"
                                    onClick={() => navigate('/questionnaire?ajustar=1')}
                                    className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-brand text-white text-sm font-bold hover:opacity-90 inline-flex items-center justify-center gap-2">
                                    <ClipboardCheck className="w-4 h-4" />
                                    {esAutogestion ? 'Solicitar la revisión' : 'Revisar mis macros'}
                                </button>
                            ) : ventana.se_abre && (
                                /* Apagado se queda VISIBLE y con su fecha: un botón gris informa,
                                   uno que no está no (tarea 7.3 del 21-08, y él lo mantiene). */
                                <button type="button" disabled data-testid="boton-revisar"
                                    className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-border bg-muted text-muted-foreground text-sm font-semibold cursor-not-allowed"
                                    data-cuando={ventana.se_abre}>
                                    {ventana.ya_enviada
                                        ? (esAutogestion ? 'Ya la has solicitado' : 'Ya lo has mandado')
                                        : `El ${cuandoSeAbre(ventana.se_abre, ventana.hora_abre)}`}
                                </button>
                            )}

                            {/* «Y LA VENTA SE LLAMA SOLICITAR AJUSTE PERSONALIZADO, como en el
                                cuestionario inicial. No "¿quieres que te lo revise yo?": la
                                misma palabra en los dos sitios» (regla 02·5).

                                Y aquí, que es donde la dibujó: pegada al próximo ajuste, «en el
                                momento en que le hace falta». Vivía dentro del editor, o sea
                                detrás de un botón, y con el nombre viejo.

                                Sigue siendo una línea y no un bloque con precio: los dos pasos
                                y los 87 € son la pantalla 08, que espera a que él decida si el
                                cobro va antes o después del reporte -- y a que cuadren el
                                catálogo (87 €) y lo que cobra la app (147 €). */}
                            {seLeOfreceLaRevision(profile, can) && (
                                <p className="text-xs text-muted-foreground border-t border-border pt-3"
                                    data-testid="revision-personalizada">
                                    <button type="button" onClick={() => navigate('/dashboard/revision')}
                                        className="underline text-brand hover:text-brand/80 font-semibold">
                                        Solicitar ajuste personalizado
                                    </button>
                                </p>
                            )}
                        </section>
                    )}

                    {/* LA CURVA DE PESO VIVE EN SEGUIMIENTO, Y SOLO AHÍ (punto 4 del 17-08).
                        «El peso no es un macro y ya vive en Evolución.»

                        Estaba en las dos pantallas y llegaron a decir lo contrario: «Ahora
                        77,1 kg · +2,1» aquí y «Ahora 50 kg · −25» allí, del mismo cliente el
                        mismo día. Se arreglaron las fuentes -- las dos leen ya la misma serie
                        saneada, punto por punto -- pero mientras la gráfica esté duplicada,
                        el día que alguien toque una de las dos volvemos a esto: de hecho, ya
                        arregladas seguían discrepando en cuántos pesajes enseñaba cada una, y
                        no se vio hasta que Francisco preguntó si de verdad coincidían.

                        Una sola gráfica no puede contradecirse consigo misma. */}

                    {/* SIN «VER MI EVOLUCIÓN» (hoja del 22-10, fila 56). La evolución se
                        consulta desde Seguimiento; aquí solo quedan los macros. */}

                    {/* El «si crees que hay que moverlos, dínoslo por el chat» murió con la
                        versión para planes con entrenador (doc 19-08): al personalizado ya no
                        se le invita a mover nada, y esta pantalla ya no la ve. */}
                </>
            )}
        </div>
    );
};

export default MisMacrosPage;
